/**
 * src/lib/api/presignedPut.js — the ONE browser -> storage PUT (TICKET-476).
 *
 * Every client upload surface (voice single PUT, voice multipart parts, quote
 * template assets, project documents) sends its bytes straight to MinIO on the
 * Hetzner box via a short-lived presigned URL. Each of those had grown its own
 * hand-rolled PUT with its own error vocabulary and no telemetry, so the same
 * failure produced four different unactionable messages and zero spine rows.
 *
 * This module owns the transport and the failure diagnosis. It deliberately
 * imports NOTHING from ./client: a presigned PUT carries no auth header and no
 * API base, and keeping it dependency-light means importing it cannot drag
 * API_BASE into a consumer (which broke partial `client` mocks when this lived
 * in minioUpload.js).
 *
 * WHY THE DIAGNOSIS IS THE POINT — TICKET-476. A PUT the browser REFUSES to send
 * is indistinguishable at the callsite from a dead storage box: XHR fires onerror
 * with status 0 and an empty body; fetch rejects with a bare "Failed to fetch".
 * Twice now the real cause was the CSP `connect-src` allow-list omitting the
 * storage origin (TICKET-473 for the STT socket, TICKET-476 for uploads), and
 * nothing was emitted either time — the spine showed the presign as `ok` and then
 * silence, so 13 days in which not one quote asset uploaded looked exactly like
 * nobody having tried. So name the likely cause in the message the user sees, and
 * emit one row per failure with the origin attached.
 */
import { logEvent } from '../observability';

/** Origin of a presigned URL. NEVER log the query string — the signature is a bearer credential. */
function originOf(url) {
  try {
    return new URL(url).origin;
  } catch {
    // spine-emit-ok: a malformed URL is not an event, and this runs INSIDE the failure
    // reporter — emitting here would recurse. The placeholder lands in the row instead.
    return 'unknown-origin';
  }
}

/**
 * Build AND report the error for a failed browser -> storage PUT.
 * @param {{step:string, url:string, status?:number, responseText?:string, cause?:unknown}} p
 * @returns {Error} throw it — the spine row is already emitted.
 */
export function reportUploadFailure({ step, url, status = 0, responseText = '', cause = null }) {
  const origin = originOf(url);
  const blocked = !status;
  const message = blocked
    ? `Upload to ${origin} never reached the network (no HTTP status). The likeliest cause is `
      + `the CSP connect-src allow-list in vercel.json not including ${origin}; otherwise `
      + 'storage is offline or the host does not resolve.'
    : `MinIO PUT failed: ${status} ${String(responseText || '').slice(0, 200)}`.trim();
  // spine-emit-ok: logEvent is fire-and-forget by contract and never throws — the
  // caller's job is to throw the Error we return, not to await telemetry.
  logEvent('storage_upload_error', message, {
    module: 'storage',
    step,
    status: 'error',
    error_code: blocked ? 'put_blocked_or_unreachable' : `http_${status}`,
    error_message: message,
    http_status: status || 0,
    origin,
    likely_csp_block: blocked,
    phase_tag: 'storage',
  });
  const err = new Error(message);
  if (cause) err.cause = cause;
  return err;
}

/**
 * PUT a File/Blob to a presigned URL with byte progress. Resolves on 2xx.
 * onProgress is called as (loaded, total). `step` names the surface in the spine.
 */
export function putToPresignedUrl({ url, file, onProgress, xhrImpl, step = 'voice_upload.put' }) {
  const XHRImpl = xhrImpl || (typeof XMLHttpRequest !== 'undefined' ? XMLHttpRequest : null);
  if (!XHRImpl) return Promise.reject(new Error('XMLHttpRequest unavailable'));
  return new Promise((resolve, reject) => {
    const xhr = new XHRImpl();
    xhr.open('PUT', url, true);
    // Content-Type is NOT part of the 3-arg presign signature (SignedHeaders=host),
    // so sending the file's type is safe and improves the stored object metadata.
    if (file.type) { try { xhr.setRequestHeader('Content-Type', file.type); } catch { /* spine-emit-ok: header set is best-effort */ } }
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => { if (onProgress) onProgress(e.loaded, e.total || file.size); };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(reportUploadFailure({ step, url, status: xhr.status, responseText: xhr.responseText }));
    };
    // status stays 0 here — no response was ever received. reportUploadFailure is
    // what turns that into an actionable message instead of "network error".
    xhr.onerror = () => reject(reportUploadFailure({ step, url }));
    xhr.send(file);
  });
}
